import { Formik, Form } from "formik";
import { useState } from "react";
import { FiArrowRight, FiAtSign, FiEye, FiEyeOff, FiLock, FiUnlock } from "react-icons/fi";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import * as Yup from "yup";
import { FormField } from "@/components/common/form-field";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { login } from "@/features/auth/api";
import { useEmailVerificationStore } from "@/features/auth/store/email-verification-store";
import { ApiError } from "@/lib/api";

const loginSchema = Yup.object({
  email: Yup.string().email("Enter a valid email").required("Email is required"),
  password: Yup.string().min(8, "Min 8 characters").required("Password is required"),
});

interface LoginValues {
  email: string;
  password: string;
  remember: boolean;
}

/**
 * Reference pattern: Formik owns form state/submission, Yup owns validation
 * rules, shadcn/FormField owns presentation. Submits to the Django auth API
 * via `features/auth/api`, which stores the session in `useAuthStore`.
 */
export function LoginForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const setPendingEmail = useEmailVerificationStore((s) => s.setPendingEmail);
  const [showPassword, setShowPassword] = useState(false);

  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;

  async function handleSubmit(values: LoginValues) {
    try {
      const result = await login(values.email, values.password);
      // Unverified account: the backend withheld tokens and re-sent an OTP.
      // Divert to the verify-email step instead of into the app.
      if (result.status === "verification_required") {
        setPendingEmail(result.email);
        toast.info("Verify your email", { description: "We've sent you a verification code." });
        navigate("/verify-email", { replace: true });
        return;
      }
      toast.success("Signed in");
      navigate(from ?? "/dashboard", { replace: true });
    } catch (err) {
      const message =
        err instanceof ApiError && err.status === 401
          ? "Invalid email or password"
          : "Couldn't sign in. Please try again.";
      toast.error(message);
    }
  }

  return (
    <Card className="gap-0 p-6 sm:p-10">
      <div className="mb-10">
        <h1 className="font-heading text-foreground mb-2 text-2xl font-bold">Welcome back</h1>
        <p className="text-muted-foreground">Sign in to access your firm&apos;s workspace.</p>
      </div>

      <Formik<LoginValues>
        initialValues={{ email: "", password: "", remember: false }}
        validationSchema={loginSchema}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting, values, setFieldValue }) => (
          <Form className="grid gap-6">
            <FormField
              name="email"
              label="Work Email"
              type="email"
              autoComplete="email"
              placeholder="name@cpafirm.com"
              icon={<FiAtSign className="size-4" />}
              className="h-11"
            />

            <FormField
              name="password"
              label="Password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              placeholder="••••••••"
              icon={<FiUnlock className="size-4" />}
              className="h-11"
              labelAdornment={
                <Link
                  to="/forgot-password"
                  className="text-info text-sm font-medium transition-colors hover:underline"
                >
                  Forgot password?
                </Link>
              }
              endAdornment={
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <FiEye className="size-4" /> : <FiEyeOff className="size-4" />}
                </button>
              }
            />

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember"
                checked={values.remember}
                onCheckedChange={(checked) => setFieldValue("remember", checked === true)}
              />
              <Label
                htmlFor="remember"
                className="text-muted-foreground cursor-pointer font-normal"
              >
                Remember this device for 30 days
              </Label>
            </div>

            <Button type="submit" variant="cta" disabled={isSubmitting} className="h-11 gap-2">
              {isSubmitting ? "Signing in..." : "Sign In"}
              <FiArrowRight className="size-4" />
            </Button>
            <p className="text-muted-foreground flex items-center justify-center gap-1.5 text-xs">
              <FiLock className="size-3 text-[color:var(--success)]" />
              We&apos;ll ask for your 6-digit code next.
            </p>
          </Form>
        )}
      </Formik>
    </Card>
  );
}
